'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NavMarker } from 'react-rhumb';
import { connect } from 'react-redux';
import BannerSection from 'pricing-pages-unified-ui/components/banners/BannerSection';
import CurrencyPicker from 'pricing-pages-unified-ui/components/common/CurrencyPicker';
import FooterMicrocopy from 'pricing-pages-unified-ui/components/common/FooterMicrocopy';
import CustomerSupport from 'pricing-pages-unified-ui/components/common/CustomerSupport';
import HubSpotPayments from 'pricing-pages-unified-ui/components/common/HubSpotPayments';
import Faq from 'pricing-pages-unified-ui/components/common/Faq';
import Question from 'pricing-pages-unified-ui/components/common/Question';
import PropTypes from 'prop-types';
import { SALES } from 'self-service-api/constants/ProductLines';
import { useEffect } from 'react';
import { SCREENS, SUBSCREENS } from '../constants/Tracking';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useAnchorLinkScroll } from '../utils/customHooks';
import { useQueryParamsEffect } from '../utils/queryParamUtils';
import { useQueryParamTerm } from '../utils/stateUtils';
import { HubCalculator } from './common/Calculator';
import HubSubtitle from './common/HubSubtitle';
import StyledBlob from './common/StyledBlob';
import TabLayout from './tabs/TabLayout';
import HubTierInfo from './product-pages/HubTierInfo';
import StandardProductPageTitle from './product-pages/StandardProductPageTitle';
import PageContainer from './common/PageContainer';
import HubInfoContainer from './common/HubInfoContainer';
import { withDocumentTitle } from 'pricing-pages-unified-ui/utils/DocumentUtils';
import PhoneSupport from 'pricing-pages-unified-ui/components/common/PhoneSupport';
import PhoneSupportFooter from 'pricing-pages-unified-ui/components/common/PhoneSupportFooter';
import FailedLoadBanner from 'pricing-pages-unified-ui/components/loading/FailedLoadBanner';
import FallbackErrorBanner from './loading/FallbackErrorBanner';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-unified-ui/selectors/LoadingStateSelectors';
import PhoneSupportWrapper from './common/phone-numbers/PhoneSupportWrapper';
import CodeSplitEnvWrapper from 'pricing-pages-unified-ui/components/CodeSplitEnvWrapper';
import CalculatorAnchorLink from 'pricing-pages-unified-ui/components/common/CalculatorAnchorLink';
import RecommendedBundlesSection from './product-pages/bundles-section/RecommendedBundlesSection';
import { TabEnum } from '../constants/TabConfig';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getSalesUrlPublic, getLanguagesUrlPublic, getCommunityUrlPublic, getKnowledgeBaseUrlPublic, getAcademyUrlPublic, getStartupsUrlPublic, getNonprofitsUrlPublic, PAYMENTS_LANDING_PAGE_URL } from 'pricing-pages-lib/utils/publicPageUrls';
import { useMon570, useMon606LogExposure } from '../selectors/ExperimentTreatmentSelectors';
import { useTrackRetail } from '../selectors/RetailSelectors';
import MON570FeatureCardWrapper from './product-pages/MON570FeatureCardWrapper';
import { getHighlightsForProductLine } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { useFeatureStore } from '../hooks/useFeatureStore';

function SalesHubPage({
  hasPricingAndPurchasingSucceeded,
  hasPricingAndPurchasingFailed
}) {
  useAnchorLinkScroll();
  useTrackRetail();
  useMon606LogExposure();
  const {
    isVariant: isMon570Variant
  } = useMon570(SALES);
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: SCREENS.PRODUCTS_PLANS,
      subscreen: SUBSCREENS.SALES_HUB
    });
  }, []);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const highlightedProducts = getHighlightsForProductLine(ProductLines.SALES);
  useFeatureStore({
    highlightedProducts,
    productLine: ProductLines.SALES
  });
  return /*#__PURE__*/_jsx(TabLayout, {
    selectedPrimaryTab: TabEnum.PRODUCTS,
    selectedSecondaryTab: TabEnum.SALES,
    children: /*#__PURE__*/_jsxs(PageContainer, {
      "data-selenium-id": "page-success",
      children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
        name: "SALES_PAGE"
      }), /*#__PURE__*/_jsx(CurrencyPicker, {}), /*#__PURE__*/_jsx(StyledBlob, {}), /*#__PURE__*/_jsxs(HubInfoContainer, {
        children: [/*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsx(StandardProductPageTitle, {
          productLine: ProductLines.SALES,
          includeRegisteredTrademark: true
        }), /*#__PURE__*/_jsxs(HubSubtitle, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricing-pages.hubs.sales.description"
          }), /*#__PURE__*/_jsx(CalculatorAnchorLink, {})]
        }), /*#__PURE__*/_jsx(BannerSection, {
          productLine: ProductLines.SALES,
          screen: SCREENS.PRODUCTS_PLANS,
          subscreen: SUBSCREENS.SALES_HUB
        }), /*#__PURE__*/_jsx(HubTierInfo, {
          productLine: ProductLines.SALES
        }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import(
          /* webpackChunkName: "public-chunk" */
          'pricing-pages-unified-ui/components/common/FreeCard'),
          publicComponentProps: {
            productLine: ProductLines.SALES
          }
        }), isMon570Variant && /*#__PURE__*/_jsx(MON570FeatureCardWrapper, {
          productLine: ProductLines.SALES
        }), /*#__PURE__*/_jsx(RecommendedBundlesSection, {
          productLine: ProductLines.SALES
        }), /*#__PURE__*/_jsx(PhoneSupportWrapper, {
          children: /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: "sales-middle",
            screen: SUBSCREENS.SALES_HUB
          })
        }), /*#__PURE__*/_jsx(HubSpotPayments, {}), /*#__PURE__*/_jsx(HubCalculator, {
          productLine: SALES,
          screen: SUBSCREENS.SALES_HUB
        }), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsxs(Faq, {
          children: [/*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatIsSalesHub411",
            href: getSalesUrlPublic()
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatIsPayments411",
            href: PAYMENTS_LANDING_PAGE_URL
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatIsFreeVsPaid411"
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.howManyUsers411",
            onClick: () => {
              document.querySelector('#calculator').scrollIntoView({
                behavior: 'smooth'
              });
              getUsageTracker().track('scrollInteraction', {
                name: 'hub-subtitle',
                action: 'clicked calculate your price'
              });
            }
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.salesVsService411"
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatSupport411",
            passedInOptions: {
              communityHref: getCommunityUrlPublic(),
              academyHref: getAcademyUrlPublic(),
              kbHref: getKnowledgeBaseUrlPublic(),
              langHref: getLanguagesUrlPublic(),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatDiscounts411",
            passedInOptions: {
              startupsHref: getStartupsUrlPublic('pricing-salespage'),
              nonprofitsHref: getNonprofitsUrlPublic('pricing-salespage'),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "sales-footer",
          screen: SUBSCREENS.SALES_HUB
        })]
      })]
    })
  });
}

SalesHubPage.propTypes = {
  hasPricingAndPurchasingSucceeded: PropTypes.bool.isRequired,
  hasPricingAndPurchasingFailed: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
  hasPricingAndPurchasingSucceeded: hasPricingAndPurchasingSucceededSelector(state),
  hasPricingAndPurchasingFailed: hasPricingAndPurchasingFailedSelector(state)
});

export default connect(mapStateToProps)(withDocumentTitle(SalesHubPage, 'sales'));